/**
 * Type-safe HTTP client for the ECS API server.
 *
 * Uses openapi-fetch with generated types from openapi.json to provide
 * compile-time path safety — a wrong URL or request body shape is a
 * compile error, not a runtime bug.
 *
 * Usage:
 *   const client = createApiClient();
 *   const { data, error } = await client.POST("/address/format", {
 *     body: { raw_address: "123 Main St", entity_name: "State Fund" },
 *   });
 */
import { message } from "antd";
import createClient, { type Middleware } from "openapi-fetch";
import { getIdToken } from "~/auth/authToken";
import { getCurrentEnv } from "~/common/utils";
import { API_SERVER_OVERRIDE_KEY, getConfig } from "~/config/configProvider";
import { errorReporter } from "~/error-handling";
import { getAutoCorrelationContext } from "~/error-handling/correlationContext";
import { ApiNetworkError } from "~/error-handling/exceptions";
import type { paths } from "./generated/api-server-types";
import type { paths as SearchServicePaths } from "./generated/search-service-types";

/**
 * How loudly a non-2xx is reported. 403 and 404 are answers, not faults — a
 * doc the caller can't open, a stale link to one that is gone — so they are
 * kept out of error-level alerting. **Warning, not info:** a 404 is also what
 * a route missing from API Gateway looks like (`cdk/lib/ecs.config.ts`), and
 * that has to stay noticeable. Everything else is an error.
 */
export function statusReportLevel(status: number): "warning" | "error" {
  return status === 403 || status === 404 ? "warning" : "error";
}

const SANDBOX_DOWN_TOAST_COOLDOWN_MS = 60_000;
let lastSandboxDownToastAt = 0;

/**
 * Show "sandbox unreachable" toast when a request to a dev sandbox fails
 * with a gateway-level "no healthy targets" signal (503/504). Debounced to
 * one toast per 60s. Skips entirely when not in dev or when no override is
 * active — deployed-dev being down is an infra alarm, not a dev-friendly
 * toast. See spec §12.6 of specs/ecs-dev-sandboxes.md.
 */
function maybeShowSandboxDownToast(status: number): void {
  if (status !== 503 && status !== 504) return;
  if (getCurrentEnv() !== "dev") return;
  let hasOverride = false;
  try {
    hasOverride = !!localStorage.getItem(API_SERVER_OVERRIDE_KEY);
  } catch {
    return;
  }
  if (!hasOverride) return;

  const now = Date.now();
  if (now - lastSandboxDownToastAt < SANDBOX_DOWN_TOAST_COOLDOWN_MS) return;
  lastSandboxDownToastAt = now;

  message.warning({
    content: "Sandbox unreachable. Run `./sandbox.sh up` (2–3 min cold start).",
    duration: 10,
  });
}

/**
 * Path portion of a request URL, for Sentry endpoint tagging. Falls back to the
 * raw URL if it can't be parsed — a parse failure must never mask the real
 * request/response error we're trying to report.
 */
function endpointPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

/**
 * Create an authenticated, type-safe API client.
 *
 * Injects a fresh Cognito JWT on every request via the fetch middleware, so the
 * client is safe to cache across a long session. getIdToken() returns cached
 * user-pool tokens and auto-refreshes on expiry — it deliberately does NOT hit
 * the Identity Pool (this client only needs the bearer JWT, never AWS creds), so
 * per-request calls can't trip the identity-pool credential throttle.
 */
export function createApiClient() {
  return createGatewayClient<paths>();
}

/**
 * The search service's client (`/search/*`). Same gateway, base URL and
 * middleware as `createApiClient`; only the generated `paths` differ.
 */
export function createSearchServiceClient() {
  return createGatewayClient<SearchServicePaths>();
}

/** Every ECS service sits behind the one API gateway; each gets its own typed paths. */
function createGatewayClient<Paths extends object>() {
  const { API_SERVER_BASE_URL } = getConfig();

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const token = await getIdToken();
      request.headers.set("Authorization", `Bearer ${token}`);

      // Attach correlation ID for end-to-end tracing through the ECS layer.
      // The ECS middleware accepts X-Correlation-ID and echoes it back.
      const ctx = getAutoCorrelationContext();
      if (ctx) {
        request.headers.set("X-Correlation-ID", ctx.correlationID);
      }

      return request;
    },
  };

  // Single canonical reporter for HTTP error *statuses*. openapi-fetch resolves
  // (does not throw) on a non-2xx response — it returns `{ data, error }` — so
  // this hook is the only place a status-level failure is seen centrally. It
  // reports every non-2xx here with status + body, and the appointment/entity
  // id is captured via the request URL in the `endpoint` tag. Service-layer
  // callers must NOT also report HTTP-status errors in their catch: the thrown
  // service Error is a different instance, so `errorReporter`'s `_isReported`
  // dedup can't suppress it and Sentry gets duplicate events. Service layers
  // should only report the cases this hook can't see — network/abort rejects
  // (handled by networkErrorMiddleware) and empty-but-ok bodies.
  const errorMiddleware: Middleware = {
    async onResponse({ request, response }) {
      if (!response.ok) {
        const body = await response.clone().text();
        errorReporter.reportApplicationError(
          new Error(
            `API server error ${response.status} on ${request.url}: ${body}`,
          ),
          {
            tags: {
              service: "api-server",
              endpoint: endpointPath(request.url),
            },
            extra: {
              statusCode: response.status,
            },
            level: statusReportLevel(response.status),
          },
        );
        maybeShowSandboxDownToast(response.status);
      }
      return response;
    },
  };

  // openapi-fetch invokes onError ONLY when fetch() itself rejects — i.e. no HTTP
  // response was ever received (transient network drop, CORS/preflight failure, or
  // an in-flight request cancelled by navigation/unmount). HTTP error *statuses*
  // resolve normally and are handled in errorMiddleware.onResponse, so they never
  // reach here. Normalize the raw TypeError/AbortError into a typed ApiNetworkError
  // so callers — and errorReporter — treat these transient, no-response failures as
  // warnings rather than hard application errors. Returning an Error makes
  // openapi-fetch throw it in place of the original.
  const networkErrorMiddleware: Middleware = {
    onError({ request, error }) {
      const aborted =
        error instanceof DOMException && error.name === "AbortError";
      return new ApiNetworkError(endpointPath(request.url), error, aborted);
    },
  };

  const client = createClient<Paths>({ baseUrl: API_SERVER_BASE_URL });
  client.use(authMiddleware);
  client.use(errorMiddleware);
  client.use(networkErrorMiddleware);
  return client;
}
